import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialDoiActions from "../../../actions/trialDoi";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    NumberFormInput,
    TextAreaFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { RestrictedLayout } from "../../../components/layouts/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { isUrl } from "../../../functions/string";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { TrialDoiDetailsBreadcrumbs } from "../../../helpers/breadcrumbHelper";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { TrialSubMenu } from "../../../helpers/subMenuHelper";
import useNavigate from "../../../hooks/useNavigate";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { ITrialDoiReduxFormState } from "../../../reducers/reactReduxForms/trialDoi";

import "./TrialDoiPage.scss";

interface TrialDoiPageProps {
    form: Dtos.TrialDoi & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reduxFormState: ITrialDoiReduxFormState;
    trialDoi: Dtos.TrialDoi;
    lookups: Dtos.Lookup[];
    loadingTrialDoi: boolean;
    loadTrialDoiSuccess: boolean;
    loadTrialDoiFailure: boolean;
    loadTrialDoiErrorCode: string;
    savingTrialDoi: boolean;
    saveTrialDoiSuccess: boolean;
    saveTrialDoiFailure: boolean;
    validatingTrialDoi: boolean;
    validationFailures: Dtos.ResponseError[];
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearTrialDoi: TrialDoiActions.ITrialDoiClearActionCreator;
    loadTrialDoi: TrialDoiActions.ITrialDoiLoadByTrialIdActionCreator;
    saveTrialDoi: TrialDoiActions.ITrialDoiSaveActionCreator;
    validateTrialDoi: TrialDoiActions.ITrialDoiValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.trialDoi";

const TrialDoiPage: FC<TrialDoiPageProps> = ({
    form,
    formState,
    formProperties,
    trialDoi,
    lookups,
    loadingTrialDoi,
    loadTrialDoiFailure,
    loadTrialDoiErrorCode,
    savingTrialDoi,
    saveTrialDoiSuccess,
    saveTrialDoiFailure,
    validatingTrialDoi,
    validationFailures,
    permissions,
    clearTrialDoi,
    loadTrialDoi,
    saveTrialDoi,
    validateTrialDoi,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const trialIdNumber = parseInt(trialId, 10);

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [formInitalised, setFormInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();


    // Load Trial Doi
    useEffect(() => {
        if (trialIdNumber && !trialDoi) {
            setBreadcrumbs(TrialDoiDetailsBreadcrumbs(trialIdNumber));
            setDrawerItems(TrialSubMenu(trialIdNumber, permissions));

            loadTrialDoi(trialIdNumber);
        } else if (trialDoi && !formInitalised) {
            loadReduxForm();
            setFormInitalised(true);
        }
    }, [
        trialIdNumber,
        trialDoi,
        permissions,
        formInitalised,
        setDrawerItems,
        setBreadcrumbs,
        loadTrialDoi,
        TrialDoiDetailsBreadcrumbs,
        setFormInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialDoiFailure) {
            navigate(`/error/${loadTrialDoiErrorCode}`);
        }
    }, [loadTrialDoiFailure, loadTrialDoiErrorCode, navigate]);

    // Update breadcrumbs
    useEffect(() => {
        if (trialIdNumber && trialDoi?.trial) {
            setBreadcrumbs(
                TrialDoiDetailsBreadcrumbs(
                    trialIdNumber,
                    trialDoi.trial?.displayName
                )
            );
        }
    }, [trialIdNumber, trialDoi, setBreadcrumbs, TrialDoiDetailsBreadcrumbs]);

    // Reload trial doi after saving
    useEffect(() => {
        if (!savingTrialDoi && saveTrialDoiSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Trial DOI saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                navigate(`/trials`);
            } else if (!trialIdNumber) {
                navigate(`/trials/${trialIdNumber}/datasharing`);
                clearTrialDoi();
            }
        }
    }, [
        savingTrialDoi,
        saveTrialDoiSuccess,
        trialIdNumber,
        navigate,
        saveAndReturn,
        clearTrialDoi,
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingTrialDoi && saveTrialDoiFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingTrialDoi, saveTrialDoiSuccess]);

    // Cleanup effect to clear the trial doi on component unmount
    useEffect(() => {
        return () => {
            clearTrialDoi();
            resetForm(reduxFormName);
        };
    }, [clearTrialDoi, resetForm, reduxFormName]);

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.TrialDoi = {} as Dtos.TrialDoi;

            if (!data.trialId) {
                changes.trialId = trialIdNumber;
            }

            await saveTrialDoi(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const handleOnSubmitFailed = (_data: Dtos.TrialDoi) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        navigate("/trials");
    };

    const loadReduxForm = () => {
        let extraData: Dtos.TrialDoi = {} as Dtos.TrialDoi;

        loadForm(reduxFormName, Object.assign({ ...trialDoi }, extraData));
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.TrialDoi = {} as Dtos.TrialDoi;

            if (form && hasBeenSubmitted && !validatingTrialDoi) {
                await validateTrialDoi(Object.assign({ ...form }, changes));
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const openLink = (link: string) => {
        window.open(`${link}`, "_blank");
        return null;
    };

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionFor(
                trialIdNumber,
                undefined,
                permissions,
                Dtos.Permission.TrialDoiUpdate,
                Dtos.Permission.TrialDoiCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingTrialDoi ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);
        const doiUrl =
            window.location.hostname.includes("localhost") ||
            window.location.hostname.includes("newtongreen")
                ? "https://doi.newtongreen.com/"
                : "https://doi.breastcancertrials.org.au/";

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Data Sharing</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-sm-12 col-xxl-2 d-flex align-items-center justify-content-start"> 
                                    {renderFormControls(
                                        form,
                                        "trialDoi",
                                        {
                                            showOnPortal: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Show on data sharing portal",
                                                },
                                                inputLabel: " ",
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 d-none",
                                                fieldClassName: "col-sm-12 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                {form.showOnPortal && (
                                    <div className="col-sm-12 col-xxl-10">
                                        <Link className={"primary-link"} to={`${doiUrl}${trialIdNumber}`}>
                                            Trial Information Data Sharing Portal
                                        </Link>
                                    </div>
                                )}
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialDoi",
                                        {
                                            ardcTrialEntry: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    addOnRightButton: (
                                                        <button
                                                            className="btn btn-secondary"
                                                            type="button"
                                                            id="button-addon2"
                                                            disabled={form?.ardcTrialEntry == null || !isUrl(form?.ardcTrialEntry)}
                                                            style={{
                                                                cursor: form?.ardcTrialEntry == null || !isUrl(form?.ardcTrialEntry)
                                                                        ? "not-allowed"
                                                                        : "pointer",
                                                                padding: "4px 10px",
                                                            }}
                                                            onClick={() => {
                                                                openLink(form.ardcTrialEntry);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.ARROWUPRIGHTFROMSQUARE} />
                                                        </button>
                                                    ),
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-xxl-2",
                                                fieldClassName: "col-sm-12 col-xxl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialDoi",
                                        {
                                            yearAvailable: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-xxl-2",
                                                fieldClassName: "col-sm-12 col-xxl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialDoi",
                                        {
                                            doi: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    addOnRightButton: (
                                                        <button
                                                            className="btn btn-secondary"
                                                            type="button"
                                                            id="button-addon2"
                                                            disabled={form?.doi == null || !isUrl(form?.doi)}
                                                            style={{
                                                                cursor: form?.doi == null || !isUrl(form?.doi)
                                                                        ? "not-allowed"
                                                                        : "pointer",
                                                                padding: "4px 10px",
                                                            }}
                                                            onClick={() => {
                                                                openLink(form.doi);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.ARROWUPRIGHTFROMSQUARE} />
                                                        </button>
                                                    ),
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-xxl-2",
                                                fieldClassName: "col-sm-12 col-xxl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-sm-12 col-xl-6">
                                    {renderFormControls(
                                        form,
                                        "trialDoi",
                                        {
                                            protocolLabel: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-xxl-4",
                                                fieldClassName: "col-sm-12 col-xxl-7 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-sm-12 col-xl-6">
                                    {renderFormControls(
                                        form,
                                        "trialDoi",
                                        {
                                            protocol: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    addOnRightButton: (
                                                        <button
                                                            className="btn btn-secondary"
                                                            type="button"
                                                            id="button-addon2"
                                                            disabled={form?.protocol == null || !isUrl(form?.protocol)}
                                                            style={{
                                                                cursor: form?.protocol == null || !isUrl(form?.protocol)
                                                                        ? "not-allowed"
                                                                        : "pointer",
                                                                padding: "4px 10px",
                                                            }}
                                                            onClick={() => {
                                                                openLink(form.protocol);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.ARROWUPRIGHTFROMSQUARE} />
                                                        </button>
                                                    ),
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-xxl-3",
                                                fieldClassName: "col-sm-12 col-xxl-9 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-sm-12 col-xl-6">
                                    {renderFormControls(
                                        form,
                                        "trialDoi",
                                        {
                                            dataDictionaryLabel: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-xxl-4",
                                                fieldClassName: "col-sm-12 col-xxl-7 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-sm-12 col-xl-6">
                                    {renderFormControls(
                                        form,
                                        "trialDoi",
                                        {
                                            dataDictionary: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    addOnRightButton: (
                                                        <button
                                                            className="btn btn-secondary"
                                                            type="button"
                                                            id="button-addon2"
                                                            disabled={form?.dataDictionary == null || !isUrl(form?.dataDictionary)}
                                                            style={{
                                                                cursor: form?.dataDictionary == null || !isUrl(form?.dataDictionary)
                                                                        ? "not-allowed"
                                                                        : "pointer",
                                                                padding: "4px 10px",
                                                            }}
                                                            onClick={() => {
                                                                openLink(form.dataDictionary);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.ARROWUPRIGHTFROMSQUARE} />
                                                        </button>
                                                    ),
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-xxl-3",
                                                fieldClassName: "col-sm-12 col-xxl-9 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialDoi",
                                        {
                                            citation: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextAreaFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-xxl-2",
                                                fieldClassName: "col-sm-12 col-xxl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <Form
                model={reduxFormName}
                onSubmit={(val, _event) => handleOnSubmit(val)}
                onSubmitFailed={(val) => handleOnSubmitFailed(val)}
            >
                {renderFormControls(
                    form,
                    "trialDoi",
                    formComponents,
                    lookups,
                    validationFailures,
                    formProperties,
                    changeForm
                )}
                <div className="mt-4 mb-4 text-right">
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", false);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit
                    </button>
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", true);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit And Return
                    </button>
                    <button onClick={onCancel} className="btn btn-primary mr-2">
                        Cancel
                    </button>
                </div>
            </Form>
        );
    }, [
        AuthenticationHelper,
        permissions,
        savingTrialDoi,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        lookups,
        validationFailures,
        formProperties,
        form,
        changeForm,
        reduxFormName,
        onCancel,
        trialDoi,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.TrialDoiView}
            loading={loadingTrialDoi || savingTrialDoi}
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<TrialDoiPageProps> => {
    let trialDoi: Dtos.TrialDoi | undefined = !(
        state.trialDoi.formData instanceof Array
    )
        ? state.trialDoi.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialDoi: trialDoi!,
        loadingTrialDoi: state.trialDoi.loadState && state.trialDoi.loadState.status == RequestState.Pending,
        loadTrialDoiSuccess: state.trialDoi.loadState && state.trialDoi.loadState.status == RequestState.Success,
        loadTrialDoiFailure: state.trialDoi.loadState && state.trialDoi.loadState.status == RequestState.Failure,
        loadTrialDoiErrorCode: state.trialDoi.loadState.errorCode,
        savingTrialDoi: state.trialDoi.saveState && state.trialDoi.saveState.status == RequestState.Pending,
        saveTrialDoiSuccess: state.trialDoi.saveState && state.trialDoi.saveState.status == RequestState.Success,
        saveTrialDoiFailure: state.trialDoi.saveState && state.trialDoi.saveState.status == RequestState.Failure,
        validatingTrialDoi: state.trialDoi.validateState && state.trialDoi.validateState.status == RequestState.Pending,
        user: state.user.data,
        permissions: permissions,
        lookups: state.trialDoi.lookups,
        form: state.reduxForms.trialDoi,
        formState: state.trialDoi.formState,
        formProperties: state.trialDoi.formProperties,
        reduxFormState: state.reduxForms.formState.trialDoi,
        validationFailures: trialDoi
            ? state.trialDoi.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<TrialDoiPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadTrialDoi: bindActionCreators(TrialDoiActions.LoadTrialDoiByTrialId, dispatch),
        clearTrialDoi: bindActionCreators(TrialDoiActions.Clear, dispatch),
        saveTrialDoi: bindActionCreators(TrialDoiActions.SaveTrialDoi, dispatch),
        validateTrialDoi: bindActionCreators(TrialDoiActions.ValidateTrialDoi, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TrialDoiPage);
